import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithPopup,
  signOut,
  type Auth,
  type User,
} from 'firebase/auth'

import { browserSupportsPasskeys, startAuthentication, startRegistration } from '@simplewebauthn/browser'

import { app } from '../firebase'
import {
  AuthApiError,
  deleteAccountRequest,
  getAuthMethods,
  getPasskeyLoginOptions,
  getPasskeyRegistrationOptions,
  listPasskeysRequest,
  removePasskeyRequest,
  startEmailCode,
  verifyEmailCode,
  verifyPasskeyLogin,
  verifyPasskeyRegistration,
  type AuthMethods,
  type PasskeySummary,
  type StartEmailCodeResult,
} from './authApi'

export type AuthUser = User

// There are no passwords here. Players sign in with Google or a one-time code
// emailed to them; both end in the same Firebase account (one uid per person),
// the same account the mobile app uses. See docs/account.md.

// Closing the Google window is a choice, not a failure.
const CANCELLED_POPUP_CODES: ReadonlySet<string> = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
])

/** Null when Firebase is not configured for this build (sign-in is then simply unavailable). */
const getFirebaseAuth = (): Auth | null => (app ? getAuth(app) : null)

export const isAuthAvailable = (): boolean => app !== null

/** The backend treats addresses case-insensitively and ignores surrounding spaces; match that. */
export const normalizeEmail = (email: string): string => email.trim().toLowerCase()

export const subscribeToAuthChanges = (callback: (user: AuthUser | null) => void): (() => void) => {
  const auth = getFirebaseAuth()
  if (!auth) {
    callback(null)
    return () => {}
  }
  return onAuthStateChanged(auth, callback)
}

/** A fresh Firebase ID token for the signed-in player, or null when nobody is signed in. */
export const getCurrentIdToken = async (): Promise<string | null> => {
  const user = getFirebaseAuth()?.currentUser
  return user ? user.getIdToken() : null
}

export const fetchAuthMethods = (): Promise<AuthMethods> => getAuthMethods()

/** Step 1 of email sign-in: ask the backend to email a 6-digit code. */
export const requestEmailCode = (email: string): Promise<StartEmailCodeResult> =>
  startEmailCode(normalizeEmail(email))

/**
 * Step 2 of email sign-in: the backend checks the code and returns a Firebase
 * custom token, which we exchange for a real Firebase session. If the code is
 * wrong nothing is signed in and the backend's error propagates.
 */
export const signInWithEmailCode = async (email: string, code: string): Promise<AuthUser> => {
  const auth = getFirebaseAuth()
  if (!auth) throw new AuthApiError({ code: 'AUTH_UNKNOWN' })

  const customToken = await verifyEmailCode(normalizeEmail(email), code)
  const credential = await signInWithCustomToken(auth, customToken)
  return credential.user
}

/** Resolves to `null` if the player closed the Google window, which is not an error. */
export const signInWithGoogle = async (): Promise<AuthUser | null> => {
  const auth = getFirebaseAuth()
  if (!auth) throw new AuthApiError({ code: 'AUTH_UNKNOWN' })

  try {
    const credential = await signInWithPopup(auth, new GoogleAuthProvider())
    return credential.user
  } catch (error) {
    const code = (error as { code?: string } | null)?.code
    if (code && CANCELLED_POPUP_CODES.has(code)) return null
    throw error
  }
}

// ---- Passkeys ---------------------------------------------------------------

/**
 * Dismissing the browser's passkey prompt is a choice, not a failure. Browsers
 * report it (and a timeout) as `NotAllowedError`, which @simplewebauthn/browser
 * passes through inside a WebAuthnError; a ceremony replaced by a newer one is
 * `ERROR_CEREMONY_ABORTED`.
 */
export const isCancelledCeremony = (error: unknown): boolean => {
  const e = error as { name?: string; code?: string; cause?: { name?: string } } | null
  return (
    e?.code === 'ERROR_CEREMONY_ABORTED' ||
    e?.name === 'NotAllowedError' ||
    e?.name === 'AbortError' ||
    e?.cause?.name === 'NotAllowedError'
  )
}

/** Whether this browser and device can use passkeys. Says nothing about whether the player has one. */
export const isPasskeySupported = async (): Promise<boolean> => {
  try {
    return await browserSupportsPasskeys()
  } catch {
    return false
  }
}

/**
 * Usernameless passkey sign-in: the device offers the passkeys it holds for
 * this site, the backend verifies the signature and returns a Firebase custom
 * token (same path as email codes, so it is the same account). Resolves to
 * `null` if the player dismissed the prompt.
 */
export const signInWithPasskey = async (): Promise<AuthUser | null> => {
  const auth = getFirebaseAuth()
  if (!auth) throw new AuthApiError({ code: 'AUTH_UNKNOWN' })

  const optionsJSON = await getPasskeyLoginOptions()
  let assertion
  try {
    assertion = await startAuthentication({ optionsJSON })
  } catch (error) {
    if (isCancelledCeremony(error)) return null
    throw error
  }

  const customToken = await verifyPasskeyLogin(assertion)
  const credential = await signInWithCustomToken(auth, customToken)
  return credential.user
}

const requireIdToken = async (): Promise<string> => {
  const idToken = await getCurrentIdToken()
  if (!idToken) throw new AuthApiError({ code: 'AUTH_INVALID_TOKEN' })
  return idToken
}

/**
 * Saves a new passkey to the signed-in account. The backend needs a recent
 * sign-in (else AUTH_REAUTH_REQUIRED). Resolves to `null` if the player
 * dismissed the prompt.
 */
export const addPasskey = async (nickname?: string): Promise<PasskeySummary | null> => {
  const idToken = await requireIdToken()
  const optionsJSON = await getPasskeyRegistrationOptions(idToken)

  let registration
  try {
    registration = await startRegistration({ optionsJSON })
  } catch (error) {
    if (isCancelledCeremony(error)) return null
    throw error
  }

  return verifyPasskeyRegistration(idToken, registration, nickname)
}

export const fetchPasskeys = async (): Promise<PasskeySummary[]> => listPasskeysRequest(await requireIdToken())

export const deletePasskey = async (id: string): Promise<void> => removePasskeyRequest(await requireIdToken(), id)

export const signOutUser = async (): Promise<void> => {
  const auth = getFirebaseAuth()
  if (auth) await signOut(auth)
}

/**
 * Permanently deletes the signed-in player's account. The backend refuses unless
 * the person signed in recently, in which case AUTH_REAUTH_REQUIRED propagates.
 * Once the account is gone the local session is cleared; a failure to clear it
 * is ignored because the account no longer exists and its token can't be used.
 *
 * Game progress stored in this browser is deliberately left alone.
 */
export const deleteAccount = async (): Promise<void> => {
  const user = getFirebaseAuth()?.currentUser
  if (!user) throw new AuthApiError({ code: 'AUTH_INVALID_TOKEN' })

  await deleteAccountRequest(await user.getIdToken())

  try {
    await signOutUser()
  } catch {
    // Already deleted server-side; nothing more to do.
  }
}
